import { useTranslation } from 'react-i18next';
import type { Client } from '@/api/clients/clientsApi.types';
import { Button } from '@/components/ui/button';
import { Icon } from '@/components/common/Icon';
import { SEARCH_DEBOUNCE_MILLISECONDS } from '@/constants/adminPagination';
import { useClientsQuery } from '@/hooks/useClientsQuery';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import { formatPhoneNumberForDisplay } from '@/utils/phoneNumbers';
import { findOwnersWithSameName } from './matchingOwners';

interface DuplicateOwnerNoticeProps {
  typedFullName: string;
  onUseExistingOwner: (client: Client) => void;
}

export function DuplicateOwnerNotice({
  typedFullName,
  onUseExistingOwner,
}: DuplicateOwnerNoticeProps) {
  const { t } = useTranslation('patients');
  const debouncedFullName = useDebouncedValue(typedFullName.trim(), SEARCH_DEBOUNCE_MILLISECONDS);
  const clientsQuery = useClientsQuery(1, debouncedFullName, '');
  const loadedClients = clientsQuery.data === undefined ? [] : clientsQuery.data.items;
  const matchingOwners = findOwnersWithSameName(debouncedFullName, loadedClients);

  if (matchingOwners.length === 0) {
    return null;
  }

  return (
    <div role="status" className="rounded-xl bg-zoowarningbg px-3 py-2.5 space-y-2">
      <p className="text-xs font-semibold text-zoowarningtext flex items-start gap-1.5">
        <Icon name="AlertCircle" className="shrink-0 mt-px" />
        {t('owner.duplicateNotice', { count: matchingOwners.length })}
      </p>
      <ul className="space-y-1.5">
        {matchingOwners.map(function renderMatchingOwner(client) {
          return (
            <li key={client.id} className="flex items-center justify-between gap-2 flex-wrap">
              <span className="text-xs text-zooindigo min-w-0">
                <span className="font-semibold">{client.fullName}</span>
                {describeContact(client)}
              </span>
              <Button
                type="button"
                variant="outline"
                size="chip"
                onClick={function useExistingOwner() {
                  onUseExistingOwner(client);
                }}
              >
                {t('owner.useExisting')}
              </Button>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function describeContact(client: Client): string {
  const contactParts = [client.email, formatPhoneNumberForDisplay(client.phone)].filter(
    function hasValue(part): part is string {
      return part !== null && part.length > 0;
    }
  );
  return contactParts.length === 0 ? '' : ' · ' + contactParts.join(' · ');
}
